import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Loader2,
  ChevronRight,
  Zap,
  Users,
} from 'lucide-react'
import { AppShell } from '@/components/shell'
import { useAuth } from '@/context/AuthContext'
import { supabase } from '@/integrations/supabase/client'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeTime(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diffMs / 60000)
  const hrs = Math.floor(diffMs / 3600000)
  const days = Math.floor(diffMs / 86400000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  if (hrs < 24) return `${hrs}h ago`
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

type Status = 'ok' | 'warn' | 'error' | 'pending' | 'none'

function Dot({ status }: { status: Status }) {
  const cls: Record<Status, string> = {
    ok: 'bg-emerald-500',
    warn: 'bg-amber-500',
    error: 'bg-red-500',
    pending: 'bg-zinc-300 dark:bg-zinc-600 animate-pulse',
    none: 'bg-zinc-200 dark:bg-zinc-700',
  }
  return <span className={`inline-block h-2.5 w-2.5 rounded-full shrink-0 ${cls[status]}`} />
}

function StatusIcon({ status, className = 'h-4 w-4' }: { status: Status; className?: string }) {
  switch (status) {
    case 'ok':
      return <CheckCircle2 className={`${className} text-emerald-500`} />
    case 'warn':
      return <AlertTriangle className={`${className} text-amber-500`} />
    case 'error':
      return <XCircle className={`${className} text-red-500`} />
    case 'pending':
      return <Clock className={`${className} text-zinc-400 dark:text-zinc-500`} />
    default:
      return <span className={`${className} inline-block rounded-full bg-zinc-200 dark:bg-zinc-700`} />
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LeagueSummary {
  leagueId: string
  leagueName: string
  competitionName: string
  season: string
  memberCount: number
  totalPipelineRows: number
  allSynced: boolean
  allScored: boolean
  allEmailed: boolean
  hasIssues: boolean
  latestCompletedMd: number | null
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function AdminStatus() {
  const { isSuperAdmin } = useAuth()

  // Last fixture sync time
  const { data: lastSync, isLoading: isLoadingSync } = useQuery({
    queryKey: ['admin-status-last-sync'],
    queryFn: async () => {
      const { data } = await supabase
        .from('sync_logs')
        .select('created_at, errors')
        .eq('sync_type', 'fixtures')
        .order('created_at', { ascending: false })
        .limit(1)
      return data?.[0] ?? null
    },
    enabled: isSuperAdmin,
    refetchInterval: 60000,
  })

  // All leagues with their configs
  const { data: leagues = [], isLoading: isLoadingLeagues } = useQuery({
    queryKey: ['admin-status-leagues'],
    queryFn: async () => {
      const { data } = await supabase
        .from('leagues')
        .select('id, name, season')
        .order('name')
      return data ?? []
    },
    enabled: isSuperAdmin,
  })

  // League configs for competition names
  const { data: leagueConfigs = [] } = useQuery({
    queryKey: ['admin-status-league-configs'],
    queryFn: async () => {
      const { data } = await supabase
        .from('league_configs')
        .select('league_id, competition_name')
      return data ?? []
    },
    enabled: isSuperAdmin,
  })

  // Pipeline status for all leagues
  const { data: allPipeline = [] } = useQuery({
    queryKey: ['admin-status-all-pipeline'],
    queryFn: async () => {
      const { data } = await supabase
        .from('pipeline_status')
        .select('league_id, matchday, season, fixtures_synced_at, results_populated_at, selections_scored_at, results_email_sent_at, errors')
      return data ?? []
    },
    enabled: isSuperAdmin,
    refetchInterval: 60000,
  })

  // Member counts per league
  const { data: memberCounts = [] } = useQuery({
    queryKey: ['admin-status-member-counts'],
    queryFn: async () => {
      const { data } = await supabase
        .from('league_memberships')
        .select('league_id')
        .eq('status', 'active')
      // Aggregate in JS
      const counts = new Map<string, number>()
      for (const m of data ?? []) {
        counts.set(m.league_id, (counts.get(m.league_id) ?? 0) + 1)
      }
      return Array.from(counts.entries()).map(([league_id, count]) => ({ league_id, count }))
    },
    enabled: isSuperAdmin,
  })

  // Build league summaries
  const leagueSummaries: LeagueSummary[] = leagues.map((league) => {
    const config = leagueConfigs.find(c => c.league_id === league.id)
    const pipelineRows = allPipeline.filter(p => p.league_id === league.id && p.season === league.season)
    const members = memberCounts.find(m => m.league_id === league.id)

    const completedRows = pipelineRows.filter(p => p.fixtures_synced_at)
    const allScored = completedRows.length > 0 && completedRows.every(p => p.selections_scored_at)
    const allEmailed = completedRows.length > 0 && completedRows.every(p => p.results_email_sent_at)
    const hasIssues = completedRows.some(p =>
      (p.fixtures_synced_at && !p.selections_scored_at) ||
      (p.selections_scored_at && !p.results_email_sent_at) ||
      (p.errors && (p.errors as unknown[]).length > 0)
    )

    const latestCompletedMd = completedRows.length > 0
      ? Math.max(...completedRows.map(p => p.matchday))
      : null

    return {
      leagueId: league.id,
      leagueName: league.name,
      competitionName: config?.competition_name ?? 'Unknown',
      season: league.season,
      memberCount: members?.count ?? 0,
      totalPipelineRows: completedRows.length,
      allSynced: completedRows.length > 0,
      allScored,
      allEmailed,
      hasIssues,
      latestCompletedMd,
    }
  })

  // Sync health
  const syncAge = lastSync ? Date.now() - new Date(lastSync.created_at).getTime() : null
  const syncStatus: Status = !syncAge
    ? 'none'
    : syncAge < 30 * 60000
      ? 'ok'
      : syncAge < 3 * 3600000
        ? 'warn'
        : 'error'
  const syncHasErrors = lastSync?.errors && lastSync.errors.length > 0

  const isLoading = isLoadingSync || isLoadingLeagues

  if (!isSuperAdmin) {
    return (
      <AppShell>
        <div className="text-center py-16 text-zinc-500 dark:text-zinc-400">
          <p className="text-lg font-medium">Access Denied</p>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="max-w-lg mx-auto px-4 pb-28 pt-4 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            System Status
          </h1>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
          </div>
        ) : (
          <>
            {/* Sync Heartbeat */}
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Dot status={syncHasErrors ? 'warn' : syncStatus} />
                  <div>
                    <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      Auto-Sync
                    </p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">
                      {lastSync
                        ? `Last run ${relativeTime(lastSync.created_at)}`
                        : 'No sync data'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Zap className="h-3.5 w-3.5 text-zinc-400" />
                  <span className="text-xs text-zinc-500 dark:text-zinc-400">
                    Every 15 min
                  </span>
                </div>
              </div>
              {syncHasErrors && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 pl-5.5">
                  Last sync had errors — check logs for details
                </p>
              )}
            </div>

            {/* Leagues */}
            <div className="space-y-2">
              <h2 className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider px-1">
                Leagues
              </h2>
              <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 divide-y divide-zinc-100 dark:divide-zinc-800">
                {leagueSummaries.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm text-zinc-400 dark:text-zinc-500">
                    No leagues found
                  </div>
                ) : (
                  leagueSummaries.map((league) => {
                    const overallStatus: Status = !league.allSynced
                      ? 'none'
                      : league.hasIssues
                        ? 'warn'
                        : league.allScored && league.allEmailed
                          ? 'ok'
                          : league.allScored
                            ? 'ok'
                            : 'pending'

                    return (
                      <Link
                        key={league.leagueId}
                        to={`/admin/leagues/${league.leagueId}`}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors group"
                      >
                        <Dot status={overallStatus} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                            {league.leagueName}
                          </p>
                          <p className="text-xs text-zinc-500 dark:text-zinc-400">
                            {league.competitionName} · {league.season}
                            {league.latestCompletedMd && (
                              <span className="ml-1.5">· MD {league.latestCompletedMd}</span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="flex items-center gap-1 text-xs text-zinc-400 dark:text-zinc-500">
                            <Users className="h-3.5 w-3.5" />
                            {league.memberCount}
                          </span>
                          <div className="flex items-center gap-1.5">
                            <StatusIcon status={league.allSynced ? 'ok' : 'none'} className="h-3.5 w-3.5" />
                            <StatusIcon status={league.allScored ? 'ok' : league.hasIssues ? 'warn' : 'none'} className="h-3.5 w-3.5" />
                            <StatusIcon status={league.allEmailed ? 'ok' : 'none'} className="h-3.5 w-3.5" />
                          </div>
                          <ChevronRight className="h-4 w-4 text-zinc-300 dark:text-zinc-600 group-hover:text-zinc-500 dark:group-hover:text-zinc-400 transition-colors" />
                        </div>
                      </Link>
                    )
                  })
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div className="space-y-2">
              <h2 className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider px-1">
                System
              </h2>
              <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 divide-y divide-zinc-100 dark:divide-zinc-800">
                {[
                  { to: '/admin/tools', label: 'Admin Tools', desc: 'Fixture sync, system operations' },
                  { to: '/admin/activity', label: 'Activity Log', desc: 'Picks, autopicks, overrides, system events' },
                  { to: '/admin/logs', label: 'Sync Logs', desc: 'Fixture sync history and errors' },
                ].map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center justify-between px-4 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors group"
                  >
                    <div>
                      <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{link.label}</p>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400">{link.desc}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-300 dark:text-zinc-600 group-hover:text-zinc-500 dark:group-hover:text-zinc-400 transition-colors" />
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </AppShell>
  )
}
